import { useEffect, useState } from "react";

const isStandalone = (): boolean =>
  globalThis.matchMedia("(display-mode: standalone)").matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

export interface InstallPromptState {
  readonly available: boolean;
  readonly installed: boolean;
  readonly isIos: boolean;
  readonly prompt: () => Promise<boolean>;
}

export const useInstallPrompt = (): InstallPromptState => {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(
    null,
  );
  const [installed, setInstalled] = useState(isStandalone);
  useEffect(() => {
    const beforeInstall = (event: BeforeInstallPromptEvent): void => {
      event.preventDefault();
      setDeferred(event);
    };
    const appInstalled = (): void => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", beforeInstall);
    window.addEventListener("appinstalled", appInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", beforeInstall);
      window.removeEventListener("appinstalled", appInstalled);
    };
  }, []);
  const prompt = async (): Promise<boolean> => {
    if (deferred === null) return false;
    await deferred.prompt();
    const result = await deferred.userChoice;
    if (result.outcome === "accepted") setDeferred(null);
    return result.outcome === "accepted";
  };
  return {
    available: deferred !== null,
    installed,
    isIos: /iPad|iPhone|iPod/.test(navigator.userAgent),
    prompt,
  };
};
